import {defineStore} from 'pinia';
import {keyBy} from 'lodash-es';
import {getCommonStoryParams} from '~/storyblok/api/options';
import {GLOBAL_BRANCH_SLUG, DEFAULT_HYPERMARKET, GLOBUS_HOME_GSOA_ID, GLOBUS_ONLINE_GSOA_ID} from '#shared/constants';
import type {Gsoa_CustomerProfile_CRMGCustomer as CRMGCustomer} from '~/openapi';
import type {Hypermarket} from '~/types/hypermarket';
import type {Branch} from '~/types/branch';
import {getBranchStoryblokRoot} from '~/utils/branch';
import {abortController} from '~/utils';

export const useStoryblokHypermarketsStore = defineStore('storyblokHypermarkets', () => {
	const {$storyblokApi, runWithContext} = useNuxtApp();
	let abort: AbortController['abort'] | null = null;
	const hypermarkets = ref<Hypermarket[] | null>(null);
	const freshBranches = ref<Branch[] | null>(null);
	const branches = ref<Branch[] | null>(null);
	const hypermarketsWithGlobus = ref<Branch[] | null>(null);

	const hypermarketsByHouseNumber = computed(() => keyBy<Branch>(branches.value ?? [], 'content.gsoaId'));
	const hypermarketsBySlug = computed(() => keyBy<Branch>(branches.value ?? [], 'slug'));
	const hypermarketsWithGlobusBySlug = computed(() => keyBy<Branch>(hypermarketsWithGlobus.value ?? [], 'slug'));
	const freshBranchesBySlug = computed(() => keyBy<Branch>(freshBranches.value ?? [], 'slug'));
	const freshBranchesWithParentHypermarket = computed(() => (freshBranches.value ?? []).filter((branch) => branch.parentHypermarketSlug));

	const selectedHypermarket = useCookie('selectedHypermarket', {maxAge: 60 * 60 * 24 * 365});

	const selectedHypermarketInstance = computed<Branch | null>(() => (
		hypermarketsBySlug.value[selectedHypermarket?.value ?? ''] ?? null
	));

	const selectedHypermarketWithFallbackInstance = computed<Branch | null>(() => (
		selectedHypermarketInstance.value ?? hypermarketsBySlug.value[DEFAULT_HYPERMARKET] ?? null
	));
	const selectedParentHypermarketInstance = computed<Branch | null>(() => {
		const parentHypermarketSlug = selectedHypermarketInstance.value?.parentHypermarketSlug;
		return parentHypermarketSlug ? hypermarketsBySlug.value[parentHypermarketSlug] ?? null : null;
	});
	const selectedParentHypermarketWithFallbackInstance = computed<Branch | null>(() => (
		selectedParentHypermarketInstance.value ?? selectedHypermarketWithFallbackInstance.value
	));

	const selectedBranchInstance = selectedHypermarketInstance;
	const selectedBranchWithFallbackInstance = selectedHypermarketWithFallbackInstance;

	const groupedBranches = computed(() => ({
		hypermarkets: hypermarkets.value ?? [],
		fresh: freshBranches.value ?? [],
	}));

	const branchOptions = computed(() => ({
		all: branches.value ?? [],
		hypermarkets: hypermarkets.value ?? [],
		fresh: freshBranches.value ?? [],
	}));

	async function fetchHypermarkets() {
		if (branches.value !== null) {
			return;
		}

		abort?.();
		const {signal, abort: newAbort} = abortController();
		if (import.meta.client) {
			abort = newAbort;
		}

		const [hypermarketStories, freshStories] = await Promise.all([
			$storyblokApi.getAll(
				'stories',
				{
					...runWithContext(getCommonStoryParams),
					content_type: 'PageTypeHypermarket',
					starts_with: '_globus-cz/_pobocky/',
					sort_by: 'content.name:asc',
				},
				undefined,
				{signal},
			) as Promise<Hypermarket[]>,
			$storyblokApi.getAll(
				'stories',
				{
					...runWithContext(getCommonStoryParams),
					content_type: 'PageTypeFresh',
					starts_with: '_globus-cz/_fresh-pobocky/',
					sort_by: 'content.name:asc',
				},
				undefined,
				{signal},
			) as Promise<Branch[]>,
		]);

		// Exclude the "Globus general" hypermarket
		const filteredStories = hypermarketStories.filter((story) => story.slug !== GLOBAL_BRANCH_SLUG);
		const globalBranch = hypermarketStories.find((story) => story.slug === GLOBAL_BRANCH_SLUG);

		// Sort: Prague branches first, then the rest
		const pragueHypermarkets = filteredStories.filter((story) => story.content.name?.toLowerCase().startsWith('praha'));
		const otherHypermarkets = filteredStories.filter((story) => !story.content.name?.toLowerCase().startsWith('praha'));
		const orderedHypermarkets = [...pragueHypermarkets, ...otherHypermarkets] as Hypermarket[];
		const normalizedHypermarkets = orderedHypermarkets.map((story) => ({
			...story,
			branchType: 'hypermarket' as const,
			baseStoryblokSlug: getBranchStoryblokRoot(story.slug),
		}));
		const hypermarketSlugByGsoaId = new Map(
			normalizedHypermarkets.map((hypermarket) => [String(hypermarket.content.gsoaId), hypermarket.slug]),
		);
		const orderedFreshBranches = freshStories
			.filter((story) => !(story as {is_folder?: boolean}).is_folder && story.slug.startsWith('fresh-'))
			.map((story) => ({
				...story,
				branchType: 'fresh' as const,
				baseStoryblokSlug: getBranchStoryblokRoot(story.slug),
				parentHypermarketSlug: hypermarketSlugByGsoaId.get(String(story.content.motherGsoaId ?? '')) ?? null,
			}));
		const normalizedGlobalBranch = globalBranch
			? {
				...globalBranch,
				branchType: 'hypermarket' as const,
				baseStoryblokSlug: getBranchStoryblokRoot(globalBranch.slug),
			}
			: null;

		hypermarkets.value = orderedHypermarkets;
		freshBranches.value = orderedFreshBranches;
		branches.value = [
			...normalizedHypermarkets,
			...orderedFreshBranches,
		];
		hypermarketsWithGlobus.value = [
			...normalizedHypermarkets,
			...orderedFreshBranches,
			...(normalizedGlobalBranch ? [normalizedGlobalBranch] : []),
		];
	}

	function setHypermarket(slug: string) {
		if (hypermarketsBySlug.value[slug]) {
			selectedHypermarket.value = slug;
		}
	}

	function setHypermarketFromProfile(profile: CRMGCustomer | null) {
		if (selectedHypermarket.value || !profile?.registration?.houseNumber) {
			return;
		}

		const houseNumber = profile.registration.houseNumber;
		const targetHouseNumber = houseNumber === GLOBUS_ONLINE_GSOA_ID ? String(GLOBUS_HOME_GSOA_ID) : String(houseNumber);
		const slug = hypermarketsByHouseNumber.value[targetHouseNumber]?.slug;

		if (slug) {
			setHypermarket(slug);
		}
	}

	return {
		hypermarkets,
		freshBranches,
		branches,
		groupedBranches,
		branchOptions,
		hypermarketsWithGlobus,
		hypermarketsWithGlobusBySlug,
		hypermarketsBySlug,
		freshBranchesBySlug,
		freshBranchesWithParentHypermarket,
		selectedHypermarket,
		selectedHypermarketInstance,
		selectedHypermarketWithFallbackInstance,
		selectedParentHypermarketInstance,
		selectedParentHypermarketWithFallbackInstance,
		selectedBranchInstance,
		selectedBranchWithFallbackInstance,
		hypermarketsByHouseNumber,
		fetchHypermarkets,
		setHypermarket,
		setHypermarketFromProfile,
	};
});
